import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type AuditEventRow = Database["public"]["Tables"]["audit_events"]["Row"];

export interface UseAuditEventsState {
  events: AuditEventRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAuditEvents(): UseAuditEventsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [events, setEvents] = useState<AuditEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (cancelled?: () => boolean): Promise<void> => {
      if (!user) {
        setEvents([]);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from("audit_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (cancelled?.()) return;
      if (fetchError) {
        setEvents([]);
        setError(fetchError.message);
      } else {
        setEvents((data as AuditEventRow[]) ?? []);
      }
      setLoading(false);
    },
    [supabase, user],
  );

  useEffect(() => {
    let stale = false;
    void refetch(() => stale);
    return () => {
      stale = true;
    };
  }, [refetch]);

  return useMemo<UseAuditEventsState>(
    () => ({ events, loading, error, refetch }),
    [events, loading, error, refetch],
  );
}
